import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Space, 
  Tag, 
  Input, 
  Select, 
  DatePicker,
  Modal,
  Form,
  InputNumber,
  message,
  Progress,
  Typography,
  Popover
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined,
  DeleteOutlined,
  EyeOutlined 
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import dayjs from 'dayjs';
import { useAuthStore } from '../../stores/authStore';

const { Search } = Input;
const { Option } = Select;
const { TextArea } = Input;

const ProjectCenter = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, isInitialized, isAuthenticated } = useAuthStore();
  
  const [searchText, setSearchText] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [designLeaderFilter, setDesignLeaderFilter] = useState(''); // 设计负责人筛选条件
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [form] = Form.useForm();

  // 设置设计负责人筛选默认值为当前用户（如果当前用户是设计师）
  useEffect(() => {
    if (user && user.role === 'designer' && !designLeaderFilter) {
      setDesignLeaderFilter(user.id.toString());
    }
  }, [user, designLeaderFilter]);

  // 获取项目列表
  const { data: projectsData, isLoading } = useQuery(
    ['projects', searchText, typeFilter, designLeaderFilter],
    () => axios.get('/projects', {
      params: {
        search: searchText || undefined,
        type: typeFilter || undefined,
        designLeaderId: designLeaderFilter || undefined,
        limit: 50
      }
    }).then(res => res.data.data),
    { refetchInterval: 30000, enabled: !!isInitialized && !!isAuthenticated }
  );

  // 获取用户列表（用于选择设计负责人）
  const { data: usersData } = useQuery(
    'users',
    () => axios.get('/auth/users', { params: { page: 1, limit: 1000 } }).then(res => res.data.data),
    { staleTime: 5 * 60 * 1000, enabled: !!isInitialized && !!isAuthenticated }
  );

  // 工具函数：根据参数键名批量获取系统参数（所有已认证用户可访问的端点）
  // 说明：后端 /system-parameters/value/:key 对所有已认证用户开放，返回 { key, value, type, description }
  // 这里将其转换为 { parameters: [{ paramKey, paramValue, paramType, description }] }，以兼容现有计算逻辑
  const fetchSystemParamsByKeys = async () => {
    const requiredKeys = [
      'scale_area',
      'threshold_budget',
      'base_refine',
      'base_bid',
      'base_apply',
      'basic_refine',
      'basic_bid',
      'basic_apply',
      'ratio_refine',
      'ratio_bid',
      'ratio_apply',
    ];
    const results = await Promise.all(
      requiredKeys.map((k) =>
        axios
          .get(`/system-parameters/value/${k}`)
          .then((res) => res.data?.data)
          .catch(() => null)
      )
    );
    const parameters = results
      .filter(Boolean)
      .map((item) => ({
        paramKey: item.key,
        paramValue: String(item.value),
        paramType: item.type,
        description: item.description,
      }));
    return { parameters };
  };

  // 是否具备系统参数列表接口访问权限（管理员、经理、项目经理）
  const canListSystemParams = ['admin', 'manager', 'pm'].includes(user?.role);

  // 获取系统参数（用于预算计算）
  const { data: systemParams } = useQuery(
    ['systemParameters', canListSystemParams],
    () =>
      canListSystemParams
        ? axios
            .get('/system-parameters', { params: { limit: 100 } })
            .then((res) => res.data.data)
        : fetchSystemParamsByKeys(),
    { staleTime: 5 * 60 * 1000, enabled: !!isInitialized && !!isAuthenticated }
  );

  // 获取用户列表（用于获取设计负责人信息）
  const { data: allUsersData } = useQuery(
    'allUsers',
    () => axios.get('/users', { params: { page: 1, limit: 1000 } }).then(res => res.data),
    {
      enabled: !!isInitialized && !!isAuthenticated,
      staleTime: 5 * 60 * 1000, // 5分钟缓存
    }
  );

  // 获取项目最后工作日志
  const { data: projectLastLogsData } = useQuery(
    ['projectLastLogs', projectsData?.projects],
    () => {
      if (!projectsData?.projects?.length) return { projectLastLogs: [] };
      const projectIds = projectsData.projects.map(p => p.id).join(',');
      return axios.get('/daily-logs/project-last-logs', {
        params: { projectIds }
      }).then(res => res.data.data);
    },
    {
      enabled: !!projectsData?.projects?.length,
      staleTime: 2 * 60 * 1000, // 2分钟缓存
    }
  );

  /**
   * 根据系统参数计算预算
   * 设计预算 = (S*X*P + C*Y*(1-P) + B) * L
   * 超过预算阈值以上部分除3后取整数值加预算阈值为设计预算
   */
  const calculateBudget = (constructionArea, projectAmount, projectType, designLeaderId) => {
     if (!systemParams?.parameters || !constructionArea || !projectAmount || !projectType) {
       return 0;
     }

    const params = systemParams.parameters;
    const getParamValue = (key) => {
      const param = params.find(p => p.paramKey === key);
      return param ? parseFloat(param.paramValue) : 0;
    };

    // 获取系统参数
    const scaleArea = getParamValue('scale_area'); // P: 面积因素占比
    const thresholdBudget = getParamValue('threshold_budget'); // 预算阈值
    
    // 根据项目类型获取对应参数
    const baseRefine = getParamValue('base_refine'); // 深化项目单位面积设计成本基数
    const baseBid = getParamValue('base_bid'); // 投标项目单位面积设计成本基数
    const baseApply = getParamValue('base_apply'); // 申报项目单位面积设计成本基数
    
    const basicRefine = getParamValue('basic_refine'); // 深化项目基础费用
    const basicBid = getParamValue('basic_bid'); // 投标项目基础费用
    const basicApply = getParamValue('basic_apply'); // 申报项目基础费用
    
    const ratioRefine = getParamValue('ratio_refine'); // 深化项目设计预算占总造价比例
    const ratioBid = getParamValue('ratio_bid'); // 投标项目设计预算占总造价比例
    const ratioApply = getParamValue('ratio_apply'); // 申报项目设计预算占总造价比例

    // 根据项目类型选择参数
    let X, Y, B;
    switch (projectType) {
      case 'deepening':
        X = baseRefine;
        Y = ratioRefine;
        B = basicRefine;
        break;
      case 'bidding':
        X = baseBid;
        Y = ratioBid;
        B = basicBid;
        break;
      case 'application':
        X = baseApply;
        Y = ratioApply;
        B = basicApply;
        break;
      default:
        X = baseBid;
        Y = ratioBid;
        B = basicBid;
    }

    // 获取设计负责人水平系数L
    let L = 1.0; // 默认值
    if (designLeaderId && allUsersData?.data?.users) {
      const designLeader = allUsersData.data.users.find(u => u.id === parseInt(designLeaderId));
      if (designLeader && designLeader.designLevelCoefficient) {
        L = parseFloat(designLeader.designLevelCoefficient);
      }
    }
    
    // 计算基础预算：(S*X*P + C*Y*(1-P) + B) * L
    const S = constructionArea;
    const C = projectAmount;
    const P = scaleArea;
    
    const baseBudget = (S * X * P + C * Y * (1 - P) + B) * L;
    
    // 如果超过预算阈值，按规则调整
    let finalBudget;
    if (baseBudget > thresholdBudget) {
      const excessAmount = baseBudget - thresholdBudget;
      finalBudget = thresholdBudget + Math.floor(excessAmount / 3);
    } else {
      finalBudget = baseBudget;
    }
    
    return Math.round(finalBudget);
  };


  const handleCreateProject = () => {
    setEditingProject(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEditProject = (project) => {
    setEditingProject(project);
    form.setFieldsValue({
      ...project,
      expectedDeliveryDate: dayjs(project.expectedDeliveryDate),
      projectAmount: project.projectAmount,
      constructionArea: project.constructionArea
    });
    setIsModalVisible(true);
  };

  const handleDeleteProject = (project) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除项目"${project.name}"吗？此操作不可恢复。`,
      okText: '确认',
      cancelText: '取消',
      onOk: () => deleteMutation.mutate(project.id)
    });
  };

  // ============== 增删改操作（useMutation）BEGIN ==============
  // 创建/更新项目
  const projectMutation = useMutation(
    async (projectData) => {
      // 中文函数级注释：根据是否有 editingProject 决定调用创建或更新接口
      if (editingProject?.id) {
        const res = await axios.put(`/projects/${editingProject.id}`, projectData);
        return res.data;
      }
      const res = await axios.post('/projects', projectData);
      return res.data;
    },
    {
      onSuccess: (res) => {
        message.success(editingProject?.id ? '项目更新成功' : '项目创建成功');
        setIsModalVisible(false);
        setEditingProject(null);
        form.resetFields();
        queryClient.invalidateQueries('projects');
      },
      onError: (error) => {
        const errMsg = error?.response?.data?.message || '操作失败，请稍后重试';
        message.error(errMsg);
      }
    }
  );

  // 删除项目
  const deleteMutation = useMutation(
    async (projectId) => {
      // 中文函数级注释：调用后端删除项目接口，按ID删除
      const res = await axios.delete(`/projects/${projectId}`);
      return res.data;
    },
    {
      onSuccess: () => {
        message.success('删除成功');
        queryClient.invalidateQueries('projects');
      },
      onError: (error) => {
        const errMsg = error?.response?.data?.message || '删除失败，请稍后重试';
        message.error(errMsg);
      }
    }
  );
  // ============== 增删改操作（useMutation）END ==============

  /**
   * 监听表单字段变化，自动计算预算
   */
  const handleFormValuesChange = (changedValues, allValues) => {
    const { constructionArea, projectAmount, type, designLeaderId } = allValues;
    
    // 当相关字段变化时自动计算预算
    if (changedValues.constructionArea || changedValues.projectAmount || 
        changedValues.type || changedValues.designLeaderId) {
      const calculatedBudget = calculateBudget(constructionArea, projectAmount, type, designLeaderId);
      if (calculatedBudget > 0) {
        form.setFieldsValue({ budget: calculatedBudget });
      }
    }
  };

  const handleModalOk = () => {
    form.validateFields().then(values => {
      const projectData = {
        ...values,
        expectedDeliveryDate: values.expectedDeliveryDate.toISOString()
      };
      projectMutation.mutate(projectData);
    });
  };



  /**
   * 根据用户ID获取真实姓名（兜底）
   * 当项目对象未包含设计负责人对象（designLeader）但包含 designLeaderId 时，
   * 通过已加载的用户列表映射出真实姓名，避免在列表中显示为数字ID。
   */
  const getUserRealNameById = (userId) => {
    if (!userId) return '';
    // 优先使用 /auth/users 接口返回的数据结构 { users: [...] }
    if (usersData?.users && Array.isArray(usersData.users)) {
      const u = usersData.users.find(u => u.id === userId || u.id === Number(userId));
      if (u) return u.realName || u.username || '';
    }
    // 其次使用 /users 接口返回的数据结构 { data: { users: [...] } }
    if (allUsersData?.data?.users && Array.isArray(allUsersData.data.users)) {
      const u = allUsersData.data.users.find(u => u.id === userId || u.id === Number(userId));
      if (u) return u.realName || u.username || '';
    }
    return '';
  };

  /**
   * 根据项目ID获取最后工作日志内容
   * 中文函数级注释：从项目最后日志数据中查找对应项目的最后工作内容
   */
  const getProjectLastWork = (projectId) => {
    if (!projectLastLogsData?.projectLastLogs) return '暂无工作记录';
    const projectLog = projectLastLogsData.projectLastLogs.find(log => log.projectId === projectId);
    return projectLog?.lastLog?.todayWork || '暂无工作记录';
  };

  /**
   * 基于项目ID获取“最新一条日志”的完整信息（用于悬停展示）
   * 中文函数级注释：从 projectLastLogsData 中找到对应项目的 lastLog 信息
   * @param {number} projectId 项目ID
   * @returns {object|null} lastLog 对象（{ log_date, todayWork, logCount, users }），若不存在返回 null
   */
  const getProjectLastLogInfo = (projectId) => {
    if (!projectLastLogsData?.projectLastLogs) return null;
    const projectLog = projectLastLogsData.projectLastLogs.find(log => log.projectId === projectId);
    return projectLog?.lastLog || null;
  };

  const getTypeText = (type) => {
    const texts = {
      bidding: '投标',
      application: '申报',
      deepening: '深化',
      consultation: '咨询',
      other: '其他'
    };
    return texts[type] || type;
  };

  const columns = [
    {
      title: '项目名称',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Button 
          type="link" 
          onClick={() => navigate(`/projects/${record.id}`)}
          style={{ padding: 0, height: 'auto' }}
        >
          {text}
        </Button>
      )
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      render: (type) => <Tag>{getTypeText(type)}</Tag>
    },

    {
      title: '进度',
      dataIndex: 'progress',
      key: 'progress',
      render: (progress) => (
        <Progress 
          percent={progress} 
          size="small"
          status={progress === 100 ? 'success' : 'active'}
        />
      )
    },
    {
      title: '设计负责人',
      dataIndex: 'designLeader',
      key: 'designLeader',
      // 兜底显示：优先显示后端联表返回的 designLeader.realName；
      // 若未返回对象但有 designLeaderId，则通过用户列表映射显示姓名；
      // 否则显示“未指定”。
      render: (leader, record) => {
        const name = leader?.realName || getUserRealNameById(record?.designLeaderId);
        return name || '未指定';
      }
    },
    {
      title: '预算',
      dataIndex: 'budget',
      key: 'budget',
      render: (budget) => `¥${budget?.toLocaleString() || 0}`
    },
    {
      title: '目前状态',
      key: 'currentStatus',
      width: 300,
      render: (_, record) => {
        const lastLog = getProjectLastLogInfo(record.id);
        const lastWork = lastLog?.todayWork || '暂无工作记录';
        const popContent = (
          <div style={{ maxWidth: 360 }}>
            <div><strong>日期：</strong>{lastLog?.log_date ? dayjs(lastLog.log_date).format('YYYY-MM-DD') : '—'}</div>
            <div><strong>日志数量：</strong>{lastLog?.logCount ?? 0}</div>
            <div><strong>参与人员：</strong>{(lastLog?.users || []).join('，') || '—'}</div>
            <div><strong>今日工作：</strong>{lastWork}</div>
          </div>
        );
        return (
          <div style={{ maxWidth: '280px' }}>
            <Popover placement="topLeft" content={popContent} title="最新日志" overlayStyle={{ maxWidth: 400 }}>
              <Typography.Text 
                ellipsis
                style={{ fontSize: '12px', color: '#666', cursor: 'pointer' }}
              >
                {lastWork}
              </Typography.Text>
            </Popover>
          </div>
        );
      }
    },
    {
      title: '预期交付',
      dataIndex: 'expectedDeliveryDate',
      key: 'expectedDeliveryDate',
      render: (date) => dayjs(date).format('YYYY-MM-DD')
    },
    {
      title: '操作',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button 
            type="text" 
            icon={<EyeOutlined />} 
            onClick={() => navigate(`/projects/${record.id}`)}
          />
          {(['admin', 'manager'].includes(user?.role) || 
            record.createdBy === user?.id) && (
            <>
              <Button 
                type="text" 
                icon={<EditOutlined />} 
                onClick={() => handleEditProject(record)}
              />
            </>
          )}
          {['admin', 'manager'].includes(user?.role) && (
            <Button 
              type="text" 
              danger
              icon={<DeleteOutlined />} 
              onClick={() => handleDeleteProject(record)}
            />
          )}
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card 
        title="项目中心"
        extra={
          ['admin', 'manager', 'pm'].includes(user?.role) && (
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={handleCreateProject}
            >
              新建项目
            </Button>
          )
        }
        className="status-card"
      >
        {/* 搜索和筛选 */}
        <div style={{ marginBottom: '16px' }}>
          <Space wrap>
            <Search
              placeholder="搜索项目名称或描述"
              allowClear
              style={{ width: 250 }}
              onSearch={setSearchText}
            />

            <Select
              placeholder="项目类型"
              allowClear
              style={{ width: 120 }}
              onChange={setTypeFilter}
            >
              <Option value="bidding">投标</Option>
              <Option value="application">申报</Option>
              <Option value="deepening">深化</Option>
              <Option value="consultation">咨询</Option>
              <Option value="other">其他</Option>
            </Select>

            <Select
              placeholder="设计负责人"
              allowClear
              style={{ width: 150 }}
              value={designLeaderFilter || undefined}
              onChange={setDesignLeaderFilter}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
              }
            >
              {allUsersData?.data?.users?.filter(u => ['designer', 'manager'].includes(u.role))
                .map(user => (
                  <Option key={user.id} value={user.id}>
                    {user.realName}
                  </Option>
                ))
              }
            </Select>
          </Space>
        </div>

        {/* 项目表格 */}
        <Table
          columns={columns}
          dataSource={projectsData?.projects || []}
          loading={isLoading}
          rowKey="id"
          pagination={{
            total: projectsData?.pagination?.total,
            pageSize: projectsData?.pagination?.limit,
            current: projectsData?.pagination?.page,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 个项目`
          }}
        />
      </Card>

      {/* 项目创建/编辑模态框 */}
      <Modal
        title={editingProject ? '编辑项目' : '新建项目'}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingProject(null);
          form.resetFields();
        }}
        confirmLoading={projectMutation.isLoading}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onValuesChange={handleFormValuesChange}
          initialValues={{
            type: 'bidding',
            budget: 0,
            progress: 0
          }}
        >
          <Form.Item
            name="name"
            label="项目名称"
            rules={[{ required: true, message: '请输入项目名称' }]}
          >
            <Input placeholder="请输入项目名称" />
          </Form.Item>

          <Form.Item
            name="type"
            label="项目类型"
            rules={[{ required: true, message: '请选择项目类型' }]}
          >
            <Select>
              <Option value="bidding">投标</Option>
              <Option value="application">申报</Option>
              <Option value="deepening">深化</Option>
              <Option value="consultation">咨询</Option>
              <Option value="other">其他</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="description"
            label="项目描述"
          >
            <TextArea rows={3} placeholder="请输入项目描述" />
          </Form.Item>

          {/* 可选字段：用于自动计算预算的辅助输入 */}
          <Form.Item
            name="projectAmount"
            label="项目总造价（元）"
            extra="用于自动计算预算（可选，亦可手动填写预算）"
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value.replace(/\$\s?|(,*)/g, '')}
              placeholder="请输入项目总造价（可选）"
            />
          </Form.Item>

          <Form.Item
            name="constructionArea"
            label="施工面积（㎡）"
            extra="用于自动计算预算（可选，亦可手动填写预算）"
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value.replace(/\$\s?|(,*)/g, '')}
              placeholder="请输入施工面积（可选）"
            />
          </Form.Item>

          <Form.Item
            name="budget"
            label="预算金额（元）"
            rules={[{ required: true, message: '请输入预算金额' }]}
            extra="预算将根据施工面积、项目总造价、项目类型等参数自动计算，也可手动调整"
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value.replace(/\$\s?|(,*)/g, '')}
              placeholder="系统将自动计算预算金额"
            />
          </Form.Item>

          <Form.Item
            name="expectedDeliveryDate"
            label="预期交付时间"
            rules={[{ required: true, message: '请选择预期交付时间' }]}
          >
            <DatePicker
              style={{ width: '100%' }}
              format="YYYY-MM-DD"
              placeholder="选择预期交付时间"
            />
          </Form.Item>

          <Form.Item
            name="designLeaderId"
            label="设计负责人"
          >
            <Select
              placeholder="选择设计负责人"
              allowClear
            >
              {allUsersData?.data?.users?.filter(u => (['designer', 'manager'].includes(u.role) || u.position === '设计部经理'))
                // 过滤掉岗位为“造价员”和“制图员”的用户，不显示在设计负责人下拉列表中
                .filter(u => !['造价员', '制图员'].includes(u.position))
                .map(user => (
                  <Option key={user.id} value={user.id}>
                    {user.realName} - {user.position}
                  </Option>
                ))}
            </Select>
          </Form.Item>



          <Form.Item
            name="clientInfo"
            label="客户信息"
          >
            <TextArea rows={2} placeholder="请输入客户信息（可选）" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ProjectCenter;
